import { useElementSize } from "@vueuse/core";
import debounce from "lodash-es/debounce";

/**
 * Checks if a div element overflows and provides a reactive flag to indicate whether
 * its content fits within its boundaries.
 *
 * You can provide the `checkDirection` to check for horizontal or vertical overflow.
 *
 * The logic is based on scroll, so for this composable to work correctly, the container div should enable scroll
 */
const useOverflow = (
  divRef: Ref<HTMLElement | null>,
  checkDirection: "x" | "y" = "x",
) => {
  const hasOverflow = ref(false);

  const checkOverflow = () => {
    if (!divRef.value) return;

    if (checkDirection === "x") {
      hasOverflow.value = divRef.value.scrollWidth > divRef.value.clientWidth;
    } else {
      hasOverflow.value = divRef.value.scrollHeight > divRef.value.clientHeight;
    }
  };

  const debouncedCheckOverflow = debounce(checkOverflow, 300);
  const { width, height } = useElementSize(divRef);

  watch([width, height], debouncedCheckOverflow, {
    immediate: true,
  });

  return { divRef, hasOverflow };
};

export { useOverflow };
